import { SITE_URL, getAbsoluteSiteUrl } from '@/data/blogSeoMetadata';

export { SITE_URL, getAbsoluteSiteUrl };

export const PNP_PAGE_PATH = '/personalised-nutrition-platform';
export const PNP_PAGE_CANONICAL_URL = getAbsoluteSiteUrl(PNP_PAGE_PATH);

export const PNP_PAGE_TITLE = 'Personalised Nutrition Platform | UK & EU | CalorieScience';
export const PNP_SOCIAL_TITLE =
  'Personalised Nutrition Platform for UK & EU Practitioners | CalorieScience';

export const PNP_META_DESCRIPTION =
  'AI-assisted personalised nutrition for UK and EU practitioners. Connect blood biomarkers, medications, intake and lifestyle to build evidence-based meal plans you review and approve.';
export const PNP_OG_DESCRIPTION =
  'Blood biomarkers, medication-linked nutrient alerts and AI-drafted meal plans you approve. EU-hosted, GDPR compliant. 30-day free trial.';
export const PNP_TWITTER_DESCRIPTION =
  'Blood biomarkers, medication-linked nutrient alerts and AI-drafted meal plans you approve. EU-hosted, GDPR compliant.';
export const PNP_SOCIAL_IMAGE_URL = getAbsoluteSiteUrl('/images/marketing/landing-feature-screens-meal-plan.webp');

export const PNP_PRIMARY_KEYWORD = 'Personalised Nutrition Platform';

export const PNP_SECONDARY_KEYWORDS = [
  'Personalised nutrition platform technology',
  'Personalised Nutrition Platform for Practitioners',
  'AI-powered personalised nutrition platforms',
  'Modern nutrition platforms',
];

export const PNP_REVIEWED_BY = 'Vandana Pramod';
export const PNP_REVIEWER_CREDENTIALS = 'MSc Personalised Nutrition';
export const PNP_LAST_REVIEWED_DATE = '2026-10-15';
export const PNP_LAST_REVIEWED_DISPLAY = '15 October 2026';
export const PNP_DEMO_VIDEO_URL = 'https://www.youtube.com/shorts/tmd1W2bKQ0Y';

export const PNP_WORKFLOW_IMAGE_DESKTOP = '/images/pnp/workflow-desktop.png';
export const PNP_WORKFLOW_IMAGE_MOBILE = '/images/pnp/workflow-mobile.png';

export type PnpFaq = {
  question: string;
  answer: string;
};

export const PNP_FAQS: PnpFaq[] = [
  {
    question: 'What is a personalised nutrition platform?',
    answer:
      "Software that helps practitioners build recommendations from an individual client's data, such as blood biomarkers, medications, intake, health conditions and lifestyle, rather than generic population advice.",
  },
  {
    question: 'Is personalised nutrition evidence-based?',
    answer:
      'Yes, and the evidence is growing. RCTs such as Food4Me found personalised advice improved diet quality more than generic advice, and a 2024 review of 7 RCTs found improvements in HbA1c and waist circumference. Results vary by outcome and population.',
  },
  {
    question: 'Does personalised nutrition replace medical care?',
    answer:
      'No. It complements medical care. Clients should keep their GP or specialist involved, especially for medical conditions and medication changes.',
  },
  {
    question: 'How does personalised meal planning work in CalorieScience?',
    answer:
      "You add the client's intake, blood results, medications and preferences. The AI drafts a plan within your parameters. You review, edit and approve it before the client receives it.",
  },
  {
    question: 'Can biomarkers improve nutrition recommendations?',
    answer:
      'They make recommendations more specific. Blood markers show where a client actually is, help prioritise nutrients, and let you track progress objectively.',
  },
  {
    question: 'Can I use genetic and microbiome tests within CalorieScience?',
    answer:
      'Not yet. CalorieScience currently supports blood and vitamin panels. Genetic, microbiome, CGM and wearable integrations are on our roadmap. Join the early-access list to hear when they launch.',
  },
  {
    question: 'Who can use CalorieScience?',
    answer:
      'Registered dietitians, nutritionists, nutritional therapists and multidisciplinary teams working with individual clients.',
  },
  {
    question: 'Is it suitable for UK and EU regulatory environments?',
    answer:
      'CalorieScience is UK and EU GDPR compliant, with client data hosted in the EU and never used to train AI. It is not a medical device. Practitioners remain responsible for clinical decisions.',
  },
  {
    question: 'Does AI replace nutrition professionals?',
    answer:
      'No. AI handles data-crunching and first drafts. Every clinical decision stays with you.',
  },
  {
    question: 'Which personalised nutrition platform is best?',
    answer:
      'It depends on your practice. For scheduling and billing alone, practice-management tools work well. If you want blood biomarkers, medication alerts and meal planning connected in one workflow, CalorieScience is built for that. Try it free for 30 days.',
  },
];

const organizationId = `${SITE_URL}/#organization`;
const reviewerId = `${SITE_URL}/#vandana-pramod`;
const websiteId = `${SITE_URL}/#website`;
const softwareId = `${SITE_URL}/#software`;
const breadcrumbId = `${PNP_PAGE_CANONICAL_URL}#breadcrumb`;

export const PNP_STRUCTURED_DATA: Record<string, unknown> = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      '@id': organizationId,
      name: 'CalorieScience',
      url: `${SITE_URL}/`,
      logo: getAbsoluteSiteUrl('/images/marketing/landing-logo-2.webp'),
      email: 'info@caloriescience.ai',
      description: 'AI-assisted clinical nutrition platform for registered dietitians, nutritionists and nutritional therapists in the UK and EU.',
      address: { '@type': 'PostalAddress', postalCode: '068914', addressCountry: 'SG' },
      founder: { '@id': reviewerId },
      sameAs: [
        'https://www.linkedin.com/company/caloriescience/',
        'https://x.com/caloriescience',
        'https://www.instagram.com/caloriescience',
      ],
    },
    {
      '@type': 'Person',
      '@id': reviewerId,
      name: PNP_REVIEWED_BY,
      jobTitle: 'Founder and CEO',
      worksFor: { '@id': organizationId },
      hasCredential: { '@type': 'EducationalOccupationalCredential', name: PNP_REVIEWER_CREDENTIALS },
      // Add sameAs when the reviewer's personal profile URL has been confirmed.
    },
    {
      '@type': 'WebSite',
      '@id': websiteId,
      url: `${SITE_URL}/`,
      name: 'CalorieScience',
      publisher: { '@id': organizationId },
      inLanguage: 'en-GB',
    },
    {
      '@type': 'WebPage',
      '@id': `${PNP_PAGE_CANONICAL_URL}#webpage`,
      url: PNP_PAGE_CANONICAL_URL,
      name: 'Personalised Nutrition Platform for UK & EU Practitioners',
      description: PNP_META_DESCRIPTION,
      isPartOf: { '@id': websiteId },
      about: { '@id': softwareId },
      breadcrumb: { '@id': breadcrumbId },
      inLanguage: 'en-GB',
      dateModified: PNP_LAST_REVIEWED_DATE,
      lastReviewed: PNP_LAST_REVIEWED_DATE,
      reviewedBy: { '@id': reviewerId },
      audience: {
        '@type': 'Audience',
        audienceType: 'Registered dietitians, nutritionists and nutritional therapists',
        geographicArea: [
          { '@type': 'Country', name: 'United Kingdom' },
          { '@type': 'AdministrativeArea', name: 'European Union' },
        ],
      },
    },
    {
      '@type': 'BreadcrumbList',
      '@id': breadcrumbId,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Personalised Nutrition Platform', item: PNP_PAGE_CANONICAL_URL },
      ],
    },
    {
      '@type': 'SoftwareApplication',
      '@id': softwareId,
      name: 'CalorieScience',
      applicationCategory: 'HealthApplication',
      operatingSystem: 'Web',
      url: `${SITE_URL}/`,
      description: 'AI-assisted clinical nutrition platform with blood biomarker analysis, medication-linked nutrient alerts and practitioner-approved meal planning.',
      featureList: [
        'AI-assisted meal planning with practitioner review',
        'Blood biomarker analysis and trends',
        'Medication-linked nutrient alerts',
        'Client assessment forms and onboarding',
        'Calendar, appointments and invoicing',
        'Client companion app',
        'EU-hosted data, UK and EU GDPR compliant',
      ],
      publisher: { '@id': organizationId },
      // Prices come from the live billing catalog; do not publish an unverified static offer.
    },

  ],
};
